'use client';

import { reportClientError } from '@/lib/reportClientError';
import { recoverToHome } from '@/lib/recoverToHome';
import { useEffect } from 'react';

function isChunkLoadErrorMessage(message: string): boolean {
  return /Loading chunk \d+ failed/i.test(message) || /ChunkLoadError/i.test(message);
}

function isNextRouterStateErrorMessage(message: string): boolean {
  return (
    /parallelRoutes\.get/i.test(message) ||
    /Cannot read properties of null \(reading 'get'\)/i.test(message) ||
    /null is not an object \(evaluating 't\.parallelRoutes\.get'\)/i.test(message)
  );
}

function isWebpackRuntimeStaleChunkMessage(message: string, stack?: string): boolean {
  // Typical symptom of stale HTML/runtime vs chunk mismatch in Next/Webpack runtime.
  // Seen as: "Cannot read properties of undefined (reading 'call')" in webpack chunk.
  const s = `${message}\n${stack || ''}`;
  return (
    /Cannot read properties of undefined \(reading 'call'\)/i.test(message) &&
    /_next\/static\/chunks\/webpack|webpack-/i.test(s)
  );
}

export default function ClientErrorRecovery({
  boundary,
  error,
}: {
  boundary: 'global' | 'root';
  error: Error & { digest?: string };
}) {
  useEffect(() => {
    try {
      reportClientError({ boundary, error });
    } catch {}

    // IMPORTANT:
    // Do NOT auto-navigate home for arbitrary runtime errors (e.g. DOM mutations by 3rd-party
    // scripts can trigger React "removeChild" errors during route changes). Auto-recovering in
    // those cases causes user-visible "bounce to home" loops.
    //
    // Instead, only trigger recover for errors that are very likely fixed by cache/SW cleanup.
    const msg = String(error?.message || '');
    const shouldAutoRecover =
      process.env.NODE_ENV === 'production' &&
      (isChunkLoadErrorMessage(msg) ||
        isNextRouterStateErrorMessage(msg) ||
        isWebpackRuntimeStaleChunkMessage(msg, error?.stack));

    if (shouldAutoRecover) {
      recoverToHome(boundary === 'global' ? 'global-error-boundary' : 'root-error-boundary');
    }
  }, [boundary, error]);

  return (
    <div className="min-h-screen bg-hzc_beige_200 flex items-center justify-center p-6">
      <div className="max-w-md w-full bg-white rounded-2xl shadow-xl p-6 text-center">
        <div className="text-lg font-semibold text-gray-900 mb-2">Bir sorun oluştu</div>
        <div className="text-sm text-gray-600">
          Geçici bir sorun yaşadık. Lütfen sayfayı yenileyin; sorun devam ederse ana sayfaya dönüp
          tekrar deneyin.
        </div>
      </div>
    </div>
  );
}

